import { MODO_PARTICIPACAO_ADVOGADO } from '@/dominio/ModoParticipacaoAdvogado';
import { action, computed, makeObservable, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import Configuracao from '../../util/Configuracao';
import ReactComponent from '../ReactComponent';
import AUTO_TEXTO_CERTIDAO from './AutoTextoCertidao';
import CamposAutoTexto from './CamposAutoTexto';
import Certidao from './Certidao';

class CamposAutoTextoWrapper extends ReactComponent {
    constructor(props) {
        super(props);

        makeObservable(this, {
            registro: computed,
            magistrados: computed,
            callbackAutoComplete: action,
            callbackAutoCompleteVotos: action,
            callbackAutoCompleteAdvogados: action,
            processarAlteracaoRedator: action
        });
    }

    get registro() {
        return this.observableProps.registro;
    }

    get magistrados() {
        if (this.registro.processo.origem === Configuracao.ORIGEM_PJE) {
            return this.dominioStore.opcoesMagistradosComposicao(this.registro);
        }
        return this.dominioStore.opcoesMagistradosAutoComplete(this.registro);
    }

    incluirComplementoAdvogado = (adv, complemento) => {
        const nomeAdv = adv ? adv.nome : '';
        const advogado = Certidao.advogados(this.registro).find((a) => {
            return a.texto && nomeAdv?.toString().toLowerCase().trim().includes(a.texto?.toLowerCase().trim());
        });

        if (advogado) {
            if (complemento === AUTO_TEXTO_CERTIDAO.REGISTRO_PRESENCA.texto) {
                if (advogado.modoParticipacao === MODO_PARTICIPACAO_ADVOGADO.TELEPRESENCIAL.codigo) {
                    complemento = AUTO_TEXTO_CERTIDAO.REGISTRO_PRESENCA_TELEPRESENCIAL.texto;
                } else if (advogado.modoParticipacao === MODO_PARTICIPACAO_ADVOGADO.VIRTUAL.codigo) {
                    complemento = AUTO_TEXTO_CERTIDAO.REGISTRO_PARTICIPACAO_VIRTUAL.texto;
                }
            }

            if (
                complemento === AUTO_TEXTO_CERTIDAO.SUSTENTACAO_ORAL.texto &&
                advogado.modoParticipacao === MODO_PARTICIPACAO_ADVOGADO.TELEPRESENCIAL.codigo
            ) {
                complemento = AUTO_TEXTO_CERTIDAO.SUSTENTACAO_ORAL_TELEPRESENCIAL.texto;
            }

            this.observableProps.callbackAutoComplete(this.replaceHashTags(complemento, advogado));
        }
    };

    incluirComplementoConteudo = (magistrado, complemento) => {
        if (magistrado) {
            this.observableProps.callbackAutoComplete(
                this.gerarTratamento(magistrado) + ' ' + magistrado.nome.trim() + complemento
            );
        }
    };

    incluirComplementoConteudoMagistradoAoFinal = (magistrado, complemento) => {
        if (magistrado) {
            this.observableProps.callbackAutoComplete(
                complemento + this.gerarTratamento(magistrado) + ' ' + magistrado.nome.trim()
            );
        }
    };

    gerarTratamento(magistrado) {
        return this.salaSessaoStore.pronomeTratamentoComCargo(magistrado);
    }

    callbackAutoComplete = async (campo, option) => {
        const valor = option?.valor;
        this.registro.rascunhoEmEdicao = true;
        this.callbackAutoCompleteVotos(campo, valor);
        this.callbackAutoCompleteAdvogados(campo, valor);
        await this.callbackAutoCompleteMagistrados(campo, valor);
    };

    callbackAutoCompleteVotos = async (campo, valor) => {
        switch (campo) {
            case AUTO_TEXTO_CERTIDAO.VOTO_VENCIDO.id:
                this.incluirComplementoConteudoMagistradoAoFinal(valor, AUTO_TEXTO_CERTIDAO.VOTO_VENCIDO.texto);
                break;
            case AUTO_TEXTO_CERTIDAO.JUNTADA_VOTO_VENCIDO.id:
                this.incluirComplementoConteudo(valor, AUTO_TEXTO_CERTIDAO.JUNTADA_VOTO_VENCIDO.texto);
                break;
            case AUTO_TEXTO_CERTIDAO.JUNTADA_VOTO_CONVERGENTE.id:
                this.incluirComplementoConteudo(valor, AUTO_TEXTO_CERTIDAO.JUNTADA_VOTO_CONVERGENTE.texto);
                break;
            default:
                break;
        }
    };

    callbackAutoCompleteAdvogados = async (campo, valor) => {
        switch (campo) {
            case AUTO_TEXTO_CERTIDAO.REGISTRO_PRESENCA.id:
                this.incluirComplementoAdvogado(valor, AUTO_TEXTO_CERTIDAO.REGISTRO_PRESENCA.texto);
                break;
            case AUTO_TEXTO_CERTIDAO.SUSTENTACAO_ORAL.id:
                this.incluirComplementoAdvogado(valor, AUTO_TEXTO_CERTIDAO.SUSTENTACAO_ORAL.texto);
                break;
            case AUTO_TEXTO_CERTIDAO.JUNTADA_PROCURACAO.id:
                this.incluirComplementoJuntadaProcuracao(valor);
                break;
            default:
                break;
        }
    };

    async callbackAutoCompleteMagistrados(campo, valor) {
        switch (campo) {
            case AUTO_TEXTO_CERTIDAO.RESSALVA_ENTENDIMENTO.id:
                this.incluirComplementoConteudo(valor, AUTO_TEXTO_CERTIDAO.RESSALVA_ENTENDIMENTO.texto);
                break;
            case AUTO_TEXTO_CERTIDAO.RESSALVA_FUNDAMENTACAO.id:
                this.incluirComplementoConteudo(valor, AUTO_TEXTO_CERTIDAO.RESSALVA_FUNDAMENTACAO.texto);
                break;
            case AUTO_TEXTO_CERTIDAO.REGISTRO_IMPEDIMENTO.id:
                this.incluirComplementoConteudoMagistradoAoFinal(valor, AUTO_TEXTO_CERTIDAO.REGISTRO_IMPEDIMENTO.texto);
                break;
            case AUTO_TEXTO_CERTIDAO.REGISTRO_AUSENCIA.id:
                this.incluirComplementoConteudoMagistradoAoFinal(valor, AUTO_TEXTO_CERTIDAO.REGISTRO_AUSENCIA.texto);
                break;
            case AUTO_TEXTO_CERTIDAO.REDATOR.id:
                await this.processarAlteracaoRedator(valor);
                break;
            default:
                break;
        }
    }

    replaceHashTags(texto, advogado) {
        const turma = this.usuarioStore.usuario.descricaoLocalizacao;
        return texto
            .replace(/#ADV_GEN2/g, advogado.genero === 'F' ? 'a' : 'o')
            .replace(/#ADV_CAP_GEN2/g, advogado.genero === 'F' ? 'a' : 'o')
            .replace(/#ADV_GEN/g, advogado.genero === 'F' ? 'a' : '')
            .replace(/#NOME_ADV/g, advogado.nome ? advogado.nome.trim() : '')
            .replace(/#DENOMINACAO_PARTE/g, advogado.denominacaoDaParte)
            .replace(/#NOME_PARTE/g, advogado.nomeDaParte ? advogado.nomeDaParte?.trim() : '')
            .replace(/#TURMA/g, turma);
    }

    incluirComplementoJuntadaProcuracao(valor) {
        this.incluirComplementoAdvogado(
            valor,
            this.salaSessaoStore.isProcessoPje(this.registro)
                ? AUTO_TEXTO_CERTIDAO.JUNTADA_PROCURACAO_PJE.texto
                : AUTO_TEXTO_CERTIDAO.JUNTADA_PROCURACAO.texto
        );
    }

    async processarAlteracaoRedator(valor) {
        if (valor) {
            this.incluirComplementoConteudo(valor, AUTO_TEXTO_CERTIDAO.REDATOR.texto);
            await this.certidaoService.salvarRedator(this.registro, valor);
        } else {
            await this.certidaoService.removerRedator(this.registro);
        }
        runInAction(() => (this.registro.processo.redator = valor));
    }

    render() {
        return (
            <CamposAutoTexto
                magistrados={this.magistrados}
                advogados={Certidao.advogados(this.registro)}
                registro={this.registro}
                callbackAutoComplete={this.callbackAutoComplete}
                id={this.observableProps.id}
                estiloGrid={this.observableProps.estiloGrid}
                listaComponentes={this.observableProps.listaComponentes}
            />
        );
    }
}

export default inject('stores')(observer(CamposAutoTextoWrapper));
